import type { FacilityMonthRollup } from './types.js'

/**
 * The series the phase 3 screens draw, each a pure read of the rollups: a month's figure for every facility chosen
 * added up, null where no chosen facility has a rollup for the month.
 */

type Picker = (rollup: FacilityMonthRollup) => number | null

/** Sums `pick` over the rollups of each period; null where there is none. */
export function sumByPeriod(rollups: readonly FacilityMonthRollup[], periods: readonly string[], pick: Picker): (number | null)[] {
  return periods.map((period) => {
    const months = rollups.filter((rollup) => rollup.period === period)
    const values = months.map(pick).filter((value): value is number => value !== null)
    return values.length === 0 ? null : values.reduce((total, value) => total + value, 0)
  })
}

/** A rate per month: `numerator` over `denominator` summed over the rollups, scaled; null where the denominator is nothing. */
export function rateByPeriod(rollups: readonly FacilityMonthRollup[], periods: readonly string[], numerator: Picker, denominator: Picker, scale = 100, decimals = 1): (number | null)[] {
  const tops = sumByPeriod(rollups, periods, numerator)
  const bottoms = sumByPeriod(rollups, periods, denominator)
  return periods.map((_, index) => {
    const top = tops[index] ?? null
    const bottom = bottoms[index] ?? null
    return top === null || bottom === null || bottom === 0 ? null : Math.round((top / bottom) * scale * 10 ** decimals) / 10 ** decimals
  })
}

export const TIER_ORDER = ['gold', 'silver', 'bronze', 'probationary'] as const
export const EMPLOYMENT_ORDER = ['full_time', 'part_time', 'prn', 'agency'] as const
export const EMPLOYMENT_LABELS: Record<(typeof EMPLOYMENT_ORDER)[number], string> = { full_time: 'Full time', part_time: 'Part time', prn: 'PRN', agency: 'Agency' }

export const FILL_ORDER = ['roster', 'offer', 'firstCome', 'assigned', 'censusAdd', 'bridge'] as const
export const FILL_LABELS: Record<(typeof FILL_ORDER)[number], string> = { roster: 'Roster at release', offer: 'Open board offers', firstCome: 'First come', assigned: 'Supervisor assigned', censusAdd: 'Census adds', bridge: 'Clinical Bridge' }

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
export const SHIFT_TYPES = ['day', 'evening', 'night'] as const
export const SHIFT_TYPE_LABELS: Record<(typeof SHIFT_TYPES)[number], string> = { day: 'Day', evening: 'Evening', night: 'Night' }

/** Call-outs by shift type (rows) and weekday (columns) over every rollup given, Monday first. */
export function callOutGrid(rollups: readonly FacilityMonthRollup[]): (number | null)[][] {
  const order = [1, 2, 3, 4, 5, 6, 0]
  return SHIFT_TYPES.map((_, shift) =>
    order.map((weekday) => {
      const cells = rollups.map((rollup) => rollup.outcomes.callOutsByWeekdayShift[weekday]?.[shift] ?? null).filter((value): value is number => value !== null)
      return cells.length === 0 ? null : cells.reduce((total, value) => total + value, 0)
    }),
  )
}

export const WEEKDAYS_MONDAY_FIRST = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/** The latest rollup of each facility in the span, for a per-facility figure. */
export function latestByFacility(rollups: readonly FacilityMonthRollup[]): Map<string, FacilityMonthRollup> {
  const latest = new Map<string, FacilityMonthRollup>()
  for (const rollup of rollups) {
    const current = latest.get(rollup.facilityId)
    if (!current || current.period < rollup.period) {
      latest.set(rollup.facilityId, rollup)
    }
  }
  return latest
}

/** The whole span added up for one facility: every tally the screens show as a number for the span. */
export function spanTotals(rollups: readonly FacilityMonthRollup[]): { overtimeFlags: number; daysInARowFlags: number; headcount: number } {
  return {
    overtimeFlags: rollups.reduce((total, rollup) => total + rollup.coverage.overtimeFlags, 0),
    daysInARowFlags: rollups.reduce((total, rollup) => total + rollup.coverage.daysInARowFlags, 0),
    headcount: Math.max(0, ...rollups.map((rollup) => EMPLOYMENT_ORDER.reduce((sum, type) => sum + rollup.workforce.headcount[type], 0))),
  }
}
